import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Send, Clock, Facebook, Instagram } from 'lucide-react';

// Custom Linktree Icon Component
const LinktreeIcon: React.FC<{ className?: string }> = ({ className = "h-10 w-10" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.73 21.01c-.59 0-1.07-.48-1.07-1.07v-4.59h-1.32v4.59c0 .59-.48 1.07-1.07 1.07s-1.07-.48-1.07-1.07v-4.59H7.88c-.59 0-1.07-.48-1.07-1.07s.48-1.07 1.07-1.07h1.32v-1.32H7.88c-.59 0-1.07-.48-1.07-1.07s.48-1.07 1.07-1.07h1.32V9.22c0-.59.48-1.07 1.07-1.07s1.07.48 1.07 1.07v1.32h1.32V9.22c0-.59.48-1.07 1.07-1.07s1.07.48 1.07 1.07v1.32h1.32c.59 0 1.07.48 1.07 1.07s-.48 1.07-1.07 1.07h-1.32v1.32h1.32c.59 0 1.07.48 1.07 1.07s-.48 1.07-1.07 1.07h-1.32v4.59c0 .59-.48 1.07-1.07 1.07z" />
  </svg>
);

const ContactPage: React.FC = () => {
  useEffect(() => {
    document.title = "Contact Us | Strum Music";
  }, []);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-red-700 py-12 text-center">
        <motion.div
          className="container-custom"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Contact Us</h1>
          <p className="text-xl text-white max-w-2xl mx-auto">Have questions or need help? We're here for you.</p>
        </motion.div>
      </section>

      {/* Main Section */}
      <section className="py-12">
        <div className="container-custom grid md:grid-cols-3 gap-8">
          {/* Info Sidebar */}
          <motion.div
            className="bg-gray-50 rounded-lg p-6 shadow-sm md:col-span-1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-xl font-bold text-black mb-6">Get In Touch</h2>
            <div className="space-y-6 text-gray-600">
              <div className="flex items-start gap-4">
                <MapPin className="h-6 w-6 text-red-700 mt-1" />
                <address className="not-italic">
                  <strong>Our Location</strong><br />
                  <a href="https://maps.app.goo.gl/EKfjTfWXRCBs7Qds9" target="_blank" rel="noopener noreferrer" className="hover:text-red-700">
                    C-7/13, Lawrence Road<br />
                    Keshav Puram, Delhi 110035<br />
                    India
                  </a>
                </address>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="h-6 w-6 text-red-700 mt-1" />
                <div><strong>Phone:</strong><br /><a href="tel:+918076883098" className="hover:text-red-700">+91-8076883098</a></div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="h-6 w-6 text-red-700 mt-1" />
                <div><strong>Email:</strong><br /><a href="mailto:strum.music.co@gmail.com" className="hover:text-red-700">strum.music.co@gmail.com</a></div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="h-6 w-6 text-red-700 mt-1" />
                <div>
                  <strong>Store Hours</strong><br />
                  Tuesday - Sunday: 11:30am - 8:30pm<br />
                  <span className="text-red-600 font-medium">Monday: Closed</span>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t mt-8">
              <h3 className="font-medium text-black mb-4">Connect With Us</h3>
              <div className="flex gap-4">
                <a href="https://facebook.com/people/Strum-music-shop/61562796578326" target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-blue-600"><Facebook className="h-5 w-5" /></a>
                <a href="https://instagram.com/strum.music.shop" target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-pink-600"><Instagram className="h-5 w-5" /></a>
                <a href="https://linktr.ee/strummusic" target="_blank" rel="noreferrer" aria-label="Linktree" className="hover:text-green-600"><LinktreeIcon className="h-5 w-5" /></a>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            className="md:col-span-2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
              <h2 className="text-xl font-bold text-black mb-6">Send Us a Message</h2>

              <form
                name="contact"
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
                className="space-y-6"
              >
                <input type="hidden" name="form-name" value="contact" />
                <input type="hidden" name="bot-field" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="text-sm font-medium">Your Name *</label>
                    <input id="name" name="name" type="text" required className="input w-full" />
                  </div>
                  <div>
                    <label htmlFor="email" className="text-sm font-medium">Email Address *</label>
                    <input id="email" name="email" type="email" required className="input w-full" />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="text-sm font-medium">Subject *</label>
                  <select id="subject" name="subject" required className="input w-full">
                    <option value="">Select a subject</option>
                    <option value="product-inquiry">Product Inquiry</option>
                    <option value="order-status">Order Status</option>
                    <option value="return-exchange">Returns & Exchanges</option>
                    <option value="technical-support">Technical Support</option>
                    <option value="rental-inquiry">Rental Inquiry</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="text-sm font-medium">Your Message *</label>
                  <textarea id="message" name="message" rows={5} required className="input w-full" />
                </div>
                <div>
                  <button type="submit" className="btn btn-primary py-2 px-6 flex items-center space-x-2">
                    <Send className="h-4 w-4" />
                    <span>Send Message</span>
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;